import React, { useState } from 'react';
import withLayout from '../../../hocs/Layout';
import { FormProvider, RHFCheckbox, RHFDateField, RHFTextField } from '../../../common/hook-form';
import { useForm } from 'react-hook-form';
import { Box, Divider, Grid, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Button } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { apiConstants, postGeneric } from '../../../common/constants';
import PageTitle from '../../../common/PageTitle';
import { useSelector } from 'react-redux';
import { convertDate } from '../../../helpers/UtilityHelper';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from "yup";
import { useSnackbar } from 'notistack';
import { FONT, SIZE, WEIGHT } from '../../../styles/foundation/typography';
import { NAVY, BORDER, WHITE, PRIMARY_BLUE, SUBTLE } from '../../../styles/foundation/colors';
import {
    pageContainerSx, pageHeroSx, heroTitleSx, heroSubSx,
    uploadSectionSx, primaryBtnSx, secondaryBtnSx, helpTextSx, helpTextTypeSx, btnRowSx,
} from '../../../styles/patterns';

// `embedded`: rendered as a tab of the Label & Invoices page, which owns the page container.
const InvoiceLabelMergeDownload = ({ type, embedded = false }) => {
    const merchantId = useSelector((state) => state.user.userInfo.merchant.id);
    const { enqueueSnackbar } = useSnackbar();
    const [isDownloadingFile, setIsDownloadingFile] = useState(false)

    const method = useForm({
        resolver: yupResolver(Yup.object().shape({
            date: Yup.date().typeError('Date is required').required('Date is required'),
            pincode: Yup.string()
                .test('valid-pincode', 'Invalid pincode', value => { if (!value) return false; const regex = /^\d{6}$/; return regex.test(value) || value.length !== 6; })
                .required("Pincode is a mandatory field")
                .test('length', 'Please enter a 6 digit pincode', value => !value || value.length === 6)
                .test('valid', 'Please enter a valid pincode', value => { if (!value || value.length !== 6) return true; return /^\d{6}$/.test(value); }),
        })),
        defaultValues: { pincode: '', date: '', sku: '', cancelled_orders: false, manifested_orders: false }
    });
    const { handleSubmit, reset } = method;

    const onSubmit = async (data) => {
        setIsDownloadingFile(true)
        try {
            const skuParam = data.sku ? `&sku=${encodeURIComponent(data.sku)}` : '';
            await postGeneric(`${apiConstants.GENERATE_BULK_INVOICE_LABEL_MERGE}?date=${convertDate(data.date)}&pincode=${data.pincode}&merchantId=${merchantId}&shipmentType=${type}&includeCancelOrders=${data.cancelled_orders}&includeManifested=${data.manifested_orders}${skuParam}`).then(response => {
                window.open(response.data?.mergedPdf, true)
            }).catch(err => {
                console.error(err);
                const message = err?.response?.data?.message || err.message
                enqueueSnackbar(message || "Error downloading file", { variant: 'error' })
                setIsDownloadingFile(false);
            });
        } catch (err) {
            const message = err?.response?.data?.message || err.message
            enqueueSnackbar(message || "Error downloading file", { variant: 'error' })
        }
        setIsDownloadingFile(false)
    }

    const typeLabel = type === 'B2B' ? 'PTL Freight' : 'D2C Freight';

    return (
        <Box sx={embedded ? undefined : pageContainerSx}>
            {!embedded && <PageTitle subTitle="Download merged invoices and shipping labels by pincode and order date">Invoice & Label Download</PageTitle>}


            {/* Form section */}
            <Box sx={uploadSectionSx}>
                {/* <Typography sx={{ fontFamily: FONT, fontSize: SIZE.md, fontWeight: WEIGHT.bold, color: NAVY, mb: 1.5 }}>
                    Filter Criteria
                </Typography> */}
                <Box sx={helpTextSx}>
                    <InfoOutlinedIcon sx={{ fontSize: 16, color: PRIMARY_BLUE, mt: 0.2 }} />
                    <Typography sx={helpTextTypeSx}>
                        <strong>How it works:</strong> Enter a 6-digit pickup pincode and order creation date.
                        Optionally include cancelled or manifested orders. The merged PDF will open in a new tab.
                    </Typography>
                </Box>

                <FormProvider methods={method} onSubmit={handleSubmit(onSubmit)}>
                    <Grid container spacing={2.5}>
                        <Grid item xs={12} sm={6} md={4}>
                            <RHFTextField label="Pincode *" name="pincode" size="small" placeholder="Enter 6-digit pincode" />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <RHFDateField label="Order Created Date" name="date" size="small" variant="outlined" />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <RHFTextField label="SKU (optional)" name="sku" size="small" placeholder="e.g. SKU123" />
                        </Grid>
                    </Grid>

                    <Typography sx={{ fontFamily: FONT, fontSize: SIZE.xs, fontWeight: WEIGHT.semibold, color: SUBTLE, mt: 2.5, mb: 1 }}>
                        Include in download
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 2 }}>
                        <RHFCheckbox name="cancelled_orders" label="Cancelled orders" />
                        <RHFCheckbox name="manifested_orders" label="Manifested orders" />
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    <Box sx={btnRowSx}>
                        <LoadingButton type="submit" loading={isDownloadingFile} variant="contained" size="small"
                            sx={primaryBtnSx} startIcon={<DownloadIcon />}>
                            Download
                        </LoadingButton>
                        <Button variant="outlined" size="small" sx={secondaryBtnSx}
                            startIcon={<RestartAltIcon />} onClick={() => reset()}>
                            Reset
                        </Button>
                    </Box>
                </FormProvider>
            </Box>
        </Box>
    )
}
export default withLayout(InvoiceLabelMergeDownload);
